import moment from "moment";
import Repository from "@/repository";

const CVRP_ROUTING_API = "/planning";
const POLL_MS = 2000;

// The solver fetches the road matrix from OSRM's table service, so plans can
// be large; the server rejects plans above this.
export const MAX_PLAN_VISITS = 500;

// A plan covers at most this many working days, within MAX_PLAN_SPREAD days
// of its date; the server checks the same.
export const MAX_PLAN_DAYS = 31;
const MAX_PLAN_SPREAD = 92;

// Distinct, readable route colours, assigned in selection order.
export const ROUTE_COLORS = [
    "#e6194b", "#3cb44b", "#4363d8", "#f58231", "#911eb4", "#46c3d6", "#f032e6", "#9a6324",
    "#808000", "#008080", "#800000", "#000075", "#e6a800", "#5c7a29", "#c2185b", "#00897b",
];

// Times are local to the planner: the solver works in LocalDateTime.
const LOCAL_DATE_TIME = "YYYY-MM-DDTHH:mm:ss";

function atTime(date, time) {
    return moment(`${date} ${time}`, "YYYY-MM-DD HH:mm").format(LOCAL_DATE_TIME);
}

function hasLocation(point) {
    return point != null && !(point.lat === 0 && point.lon === 0);
}

function positiveOr(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
}

function nonNegativeOr(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) && number >= 0 ? number : fallback;
}

function isClock(value) {
    return typeof value === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

// The first value that is set: a planner edit, then the record's details.
function firstSet(...values) {
    return values.find(value => value !== undefined && value !== null && value !== "");
}

let pollTimer = null;

const defaultSettings = () => ({
    planDate: moment().format("YYYY-MM-DD"),
    departureTime: "07:30",
    // latest return for every vehicle; empty for no limit
    shiftEnd: "",
    windowStart: "08:00",
    windowEnd: "18:00",
    serviceMinutes: 20,
    defaultCapacity: 10,
    defaultDemand: 1,
    // where routes start and end when a vehicle has no depot of its own; null
    // starts each vehicle where it is now and brings it back there
    depotPlaceId: null,
    costPerKm: 0,
    costPerHour: 0,
    fixedCost: 0,
    spentLimit: 30,
    // Plans over several days: the number of working days from the plan date
    // and which weekdays count (0 = Sunday). Stops that do not fit a day carry
    // over to the next.
    planDays: 1,
    workWeekdays: [1, 2, 3, 4, 5, 6],
    // vehicles go back to their depot to unload when full and carry on
    returnTrips: false,
    reloadMinutes: 15,
    // plan times are local to this zone; saved so progress tracking matches
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

const state = {
    settings: defaultSettings(),

    selectedVehicles: [],
    selectedVisits: [],

    // Per-vehicle and per-place edits made in the planner, keyed by id. They
    // win over the vehicle/place record and survive switching tabs.
    vehicleOverrides: {},
    visitOverrides: {},

    jobId: null,
    solving: false,
    // The solver's latest answer; null until a solve has been started.
    vehicleRoutePlan: null,
    // getProblem serialised as it was when the shown solution was produced; an
    // edit that changes the problem makes the solution stale.
    solutionBaseline: null,

    // The saved plan open in the planner (summary fields only), and the save
    // key it was loaded or saved with, to tell when there are unsaved changes.
    currentSnapshot: null,
    savedKey: null,
    savedPlans: [],
    savedPlansLoaded: false,
    savedTemplates: [],

    // Planned against actual for the open saved plan, when tracked.
    progress: null,

    // The day of a plan over several that the map and timelines show
    // ("YYYY-MM-DD"); null shows all of them on the map and the first in the
    // timelines.
    viewDay: null,
};

const getters = {
    // The vehicles that can be planned: a tracked vehicle with a known position,
    // which is where its route starts unless it has a depot.
    getPlannableDevices: (state, getters, rootState) => {
        return rootState.vehicles.devices.device_data.filter(device => device.vehicle && hasLocation(device.last_location_point));
    },

    getPlaceMap: (state, getters, rootState) => {
        const places = new Map();
        rootState.place.place_data.forEach(place => places.set(place.id, place));
        return places;
    },

    // Places marked as hubs, offered first as depots.
    getDepots: (state, getters, rootState) => rootState.place.place_data
        .filter(place => place.is_hub && hasLocation(place.location))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),

    // Vehicle types a place can require: every type in the fleet plus any
    // typed in the planner.
    getVehicleTypes: (state, getters, rootState) => {
        const types = new Set();
        rootState.vehicles.devices.device_data.forEach(device => {
            if (device.vehicle && device.vehicle.type) types.add(device.vehicle.type);
            const details = device.vehicle && device.vehicle.details;
            ((details && details.skills) || []).forEach(skill => types.add(skill));
        });
        Object.values(state.vehicleOverrides).forEach(override => (override.skills || []).forEach(skill => types.add(skill)));
        return [...types].sort();
    },

    getVehicleColor: state => vehicleId => {
        const index = state.selectedVehicles.indexOf(vehicleId);
        return ROUTE_COLORS[(index < 0 ? 0 : index) % ROUTE_COLORS.length];
    },

    // Everything the solver needs about one vehicle, from the planner edit,
    // else the vehicle's saved details, else the plan settings.
    getVehicleParams: state => device => {
        const override = state.vehicleOverrides[device.vehicle_id] || {};
        const details = (device.vehicle && device.vehicle.details) || {};
        const settings = state.settings;
        const defaultSkills = device.vehicle && device.vehicle.type ? [device.vehicle.type] : [];
        return {
            capacity: positiveOr(firstSet(override.capacity, details.capacity), settings.defaultCapacity),
            maxWeight: firstSet(override.maxWeight, details.max_weight) != null ? Number(firstSet(override.maxWeight, details.max_weight)) : null,
            maxVolume: firstSet(override.maxVolume, details.max_volume) != null ? Number(firstSet(override.maxVolume, details.max_volume)) : null,
            // "" in an override means "start where it is", overriding a depot
            depotPlaceId: override.depotPlaceId !== undefined ? (override.depotPlaceId || null)
                : firstSet(details.depot_id, settings.depotPlaceId) || null,
            departureTime: isClock(override.departureTime) ? override.departureTime
                : isClock(details.shift_start) ? details.shift_start : settings.departureTime,
            shiftEnd: override.shiftEnd !== undefined ? override.shiftEnd
                : isClock(details.shift_end) ? details.shift_end : settings.shiftEnd,
            skills: override.skills || details.skills || defaultSkills,
            costPerKm: nonNegativeOr(firstSet(override.costPerKm, details.cost_per_km), settings.costPerKm),
            costPerHour: nonNegativeOr(firstSet(override.costPerHour, details.cost_per_hour), settings.costPerHour),
            fixedCost: nonNegativeOr(firstSet(override.fixedCost, details.fixed_cost), settings.fixedCost),
        };
    },

    // A place record's demand of 0 is how "never set" is stored, so it falls
    // back to the default; a 0 typed in the planner is kept.
    getVisitParams: state => place => {
        const override = state.visitOverrides[place.id] || {};
        const details = place.details || {};
        const settings = state.settings;
        return {
            demand: override.demand != null ? override.demand : positiveOr(details.demand, settings.defaultDemand),
            weight: nonNegativeOr(firstSet(override.weight, details.weight), 0),
            volume: nonNegativeOr(firstSet(override.volume, details.volume), 0),
            serviceMinutes: nonNegativeOr(firstSet(override.serviceMinutes, details.service_minutes), settings.serviceMinutes),
            openTime: isClock(override.openTime) ? override.openTime : isClock(details.open_time) ? details.open_time : settings.windowStart,
            closeTime: isClock(override.closeTime) ? override.closeTime : isClock(details.close_time) ? details.close_time : settings.windowEnd,
            requiredTypes: override.requiredTypes || details.required_vehicle_types || [],
        };
    },

    // The working days the plan covers, from the plan date: the plan date
    // itself and the next planDays - 1 working weekdays.
    getPlanDates: state => {
        const settings = state.settings;
        const start = moment(settings.planDate, "YYYY-MM-DD");
        const count = Math.min(MAX_PLAN_DAYS, Math.max(1, Math.round(Number(settings.planDays) || 1)));
        const weekdays = new Set(settings.workWeekdays || []);
        const dates = [start.format("YYYY-MM-DD")];
        if (weekdays.size === 0) return dates;
        for (let offset = 1; dates.length < count && offset <= MAX_PLAN_SPREAD; offset++) {
            const day = start.clone().add(offset, "days");
            if (weekdays.has(day.day())) dates.push(day.format("YYYY-MM-DD"));
        }
        return dates;
    },

    // the same days as offsets from the plan date, as the solver takes them
    getDayOffsets: (state, getters) => {
        const start = moment(state.settings.planDate, "YYYY-MM-DD");
        return getters.getPlanDates.map(date => moment(date, "YYYY-MM-DD").diff(start, "days"));
    },

    getVehicleCapacity: (state, getters) => device => getters.getVehicleParams(device).capacity,
    getVisitDemand: (state, getters) => place => getters.getVisitParams(place).demand,
    getVisitServiceMinutes: (state, getters) => place => getters.getVisitParams(place).serviceMinutes,

    // One place as the solver expects a visit.
    toVisit: (state, getters) => place => {
        const params = getters.getVisitParams(place);
        const date = state.settings.planDate;
        const visit = {
            id: place.id,
            name: place.name,
            location: [place.location.lat, place.location.lon],
            demand: Math.round(nonNegativeOr(params.demand, 0)),
            min_start_time: atTime(date, params.openTime),
            max_end_time: atTime(date, params.closeTime),
            service_duration: Math.round(params.serviceMinutes * 60),
        };
        if (params.weight > 0) visit.weight = params.weight;
        if (params.volume > 0) visit.volume = params.volume;
        if (params.requiredTypes.length) visit.required_skills = params.requiredTypes;
        return visit;
    },

    // The problem exactly as the timefold service expects it.
    getProblem: (state, getters) => {
        const settings = state.settings;
        const date = settings.planDate;

        const devices = new Map(getters.getPlannableDevices.map(device => [device.vehicle_id, device]));
        const dayOffsets = getters.getDayOffsets;
        const vehicles = state.selectedVehicles
            .filter(id => devices.has(id))
            .map(id => {
                const device = devices.get(id);
                const params = getters.getVehicleParams(device);
                const depot = params.depotPlaceId ? getters.getPlaceMap.get(params.depotPlaceId) : null;
                const start = depot && hasLocation(depot.location) ? depot.location : device.last_location_point;
                const vehicle = {
                    id: id,
                    name: device.vehicle.name,
                    capacity: Math.round(positiveOr(params.capacity, 1)),
                    color: getters.getVehicleColor(id),
                    home_location: [start.lat, start.lon],
                    departure_time: atTime(date, params.departureTime),
                    visits: [],
                };
                // a vehicle that starts at a depot returns to it
                if (depot && hasLocation(depot.location)) vehicle.end_location = [depot.location.lat, depot.location.lon];
                if (isClock(params.shiftEnd) && params.shiftEnd > params.departureTime) vehicle.shift_end_time = atTime(date, params.shiftEnd);
                if (params.maxWeight != null && params.maxWeight > 0) vehicle.max_weight = params.maxWeight;
                if (params.maxVolume != null && params.maxVolume > 0) vehicle.max_volume = params.maxVolume;
                if (params.skills.length) vehicle.skills = params.skills;
                if (params.costPerKm > 0) vehicle.cost_per_km = params.costPerKm;
                if (params.costPerHour > 0) vehicle.cost_per_hour = params.costPerHour;
                if (params.fixedCost > 0) vehicle.fixed_cost = params.fixedCost;
                if (dayOffsets.length > 1) vehicle.day_offsets = dayOffsets;
                if (settings.returnTrips) {
                    vehicle.return_trips = true;
                    vehicle.reload_duration = Math.round(nonNegativeOr(settings.reloadMinutes, 0) * 60);
                }
                return vehicle;
            });

        const visits = state.selectedVisits
            .map(id => getters.getPlaceMap.get(id))
            .filter(place => place && hasLocation(place.location))
            .map(place => getters.toVisit(place));

        return {
            name: `Plan ${settings.planDate}`,
            spent_limit: Math.round(positiveOr(settings.spentLimit, 30)),
            vehicles: vehicles,
            visits: visits,
        };
    },

    // The days the solution uses, in order, for the day pickers.
    getSolutionDays: state => {
        const solution = state.vehicleRoutePlan;
        if (!solution) return [];
        const dates = new Set();
        solution.vehicles.forEach(vehicle => (vehicle.trips || []).forEach(trip => dates.add(trip.date)));
        return [...dates].sort().map(date => ({date, label: moment(date, "YYYY-MM-DD").format("ddd D MMM")}));
    },

    // The chosen day while the solution still uses it, else null (all days).
    getViewDay: (state, getters) => getters.getSolutionDays.some(day => day.date === state.viewDay) ? state.viewDay : null,

    // The day the timelines show: the chosen one, else the first; null for a
    // plan of one day, which they show whole.
    getTimelineDay: (state, getters) => {
        const days = getters.getSolutionDays;
        if (days.length < 2) return null;
        return getters.getViewDay || days[0].date;
    },

    // What the map and timelines draw: the solution once there is one,
    // otherwise the problem being built.
    getDisplayPlan: (state, getters) => state.vehicleRoutePlan || getters.getProblem,

    // what the fleet can carry over the whole plan, one load per vehicle a day
    // (with return trips there is no such limit)
    getTotalVehicleCapacity: (state, getters) => getters.getProblem.vehicles.reduce((sum, vehicle) => sum + vehicle.capacity, 0) *
        getters.getPlanDates.length,
    getTotalVisitDemand: (state, getters) => getters.getProblem.visits.reduce((sum, visit) => sum + visit.demand, 0),

    // Costs are compared in money only when every vehicle has them; a mix
    // would weigh some routes in money and others in seconds plus metres.
    getMixedCosts: (state, getters) => {
        const vehicles = getters.getProblem.vehicles;
        const priced = vehicles.filter(vehicle => vehicle.cost_per_km || vehicle.cost_per_hour || vehicle.fixed_cost).length;
        return priced > 0 && priced < vehicles.length;
    },

    // Reasons the plan cannot be solved yet, in the order the user should fix them.
    getProblemIssues: (state, getters) => {
        const problem = getters.getProblem;
        const issues = [];
        if (problem.vehicles.length === 0) issues.push("Select at least one vehicle");
        if (problem.visits.length === 0) issues.push("Select at least one place");
        if (problem.visits.length > MAX_PLAN_VISITS) issues.push(`Select at most ${MAX_PLAN_VISITS} places (${problem.visits.length} selected)`);
        if (problem.visits.some(visit => visit.max_end_time <= visit.min_start_time)) issues.push("A place's visit window ends before it starts");
        if (getters.getMixedCosts) issues.push("Give every vehicle costs, or none");
        if (state.settings.planDays > 1 && !(state.settings.workWeekdays || []).length) issues.push("Choose the weekdays vehicles work");
        return issues;
    },

    canEdit: (state, getters, rootState, rootGetters) => rootGetters["profile/getEditPermission"]("planning"),

    // The planner state a saved plan restores.
    getSelection: state => ({
        selectedVehicles: state.selectedVehicles,
        selectedVisits: state.selectedVisits,
        vehicleOverrides: state.vehicleOverrides,
        visitOverrides: state.visitOverrides,
        settings: state.settings,
    }),

    getSaveKey: (state, getters) => JSON.stringify({
        selection: getters.getSelection,
        score: state.vehicleRoutePlan ? state.vehicleRoutePlan.score : null,
        distance: state.vehicleRoutePlan ? state.vehicleRoutePlan.total_driving_distance : null,
    }),

    hasUnsavedChanges: (state, getters) => {
        if (!state.currentSnapshot) {
            return state.selectedVisits.length > 0 || state.vehicleRoutePlan != null;
        }
        return getters.getSaveKey !== state.savedKey;
    },

    // Published and completed plans keep their routes; the server refuses edits.
    isSnapshotLocked: state => state.currentSnapshot != null &&
        ["published", "completed"].includes(state.currentSnapshot.status),

    getSolvedVehicle: state => vehicleId => {
        if (!state.vehicleRoutePlan) return null;
        return state.vehicleRoutePlan.vehicles.find(vehicle => vehicle.id === vehicleId) || null;
    },

    getSolvedVisit: state => visitId => {
        if (!state.vehicleRoutePlan) return null;
        return state.vehicleRoutePlan.visits.find(visit => visit.id === visitId) || null;
    },
};

const mutations = {
    setVehicleRoutePlan(state, vehicleRoutePlan) {
        state.vehicleRoutePlan = vehicleRoutePlan;
    },

    setViewDay(state, payload) {
        state.viewDay = payload;
    },

    setJobId(state, payload) {
        state.jobId = payload;
    },

    setSolving(state, payload) {
        state.solving = payload;
    },

    updateSettings(state, payload) {
        state.settings = Object.assign({}, state.settings, payload);
    },

    toggleVehicle(state, vehicleId) {
        const index = state.selectedVehicles.indexOf(vehicleId);
        if (index > -1) {
            state.selectedVehicles.splice(index, 1);
        } else {
            state.selectedVehicles.push(vehicleId);
        }
    },

    toggleVisit(state, visitId) {
        const index = state.selectedVisits.indexOf(visitId);
        if (index > -1) {
            state.selectedVisits.splice(index, 1);
        } else {
            state.selectedVisits.push(visitId);
        }
    },

    setSelectedVehicles(state, payload) {
        state.selectedVehicles = [...new Set(payload)];
    },

    setSelectedVisits(state, payload) {
        state.selectedVisits = [...new Set(payload)];
    },

    setVehicleOverride(state, {id, field, value}) {
        const current = state.vehicleOverrides[id] || {};
        state.vehicleOverrides = Object.assign({}, state.vehicleOverrides, {[id]: Object.assign({}, current, {[field]: value})});
    },

    setVisitOverride(state, {id, field, value}) {
        const current = state.visitOverrides[id] || {};
        state.visitOverrides = Object.assign({}, state.visitOverrides, {[id]: Object.assign({}, current, {[field]: value})});
    },

    setSolutionBaseline(state, payload) {
        state.solutionBaseline = payload;
    },

    setCurrentSnapshot(state, {snapshot, savedKey}) {
        state.currentSnapshot = snapshot;
        state.savedKey = savedKey;
    },

    setSavedPlans(state, payload) {
        state.savedPlans = payload;
        state.savedPlansLoaded = true;
    },

    setSavedTemplates(state, payload) {
        state.savedTemplates = payload;
    },

    setProgress(state, payload) {
        state.progress = payload;
    },

    // Planner edits for several vehicles or places at once, from the
    // parameter dialogs' "apply to all selected".
    setVehicleOverrides(state, {ids, values}) {
        const next = Object.assign({}, state.vehicleOverrides);
        ids.forEach(id => { next[id] = Object.assign({}, next[id] || {}, values); });
        state.vehicleOverrides = next;
    },

    setVisitOverrides(state, {ids, values}) {
        const next = Object.assign({}, state.visitOverrides);
        ids.forEach(id => { next[id] = Object.assign({}, next[id] || {}, values); });
        state.visitOverrides = next;
    },

    restoreSelection(state, selection) {
        state.selectedVehicles = [...(selection.selectedVehicles || [])];
        state.selectedVisits = [...(selection.selectedVisits || [])];
        state.vehicleOverrides = Object.assign({}, selection.vehicleOverrides || {});
        state.visitOverrides = Object.assign({}, selection.visitOverrides || {});
        state.settings = Object.assign(defaultSettings(), selection.settings || {});
    },

    reset(state) {
        state.selectedVehicles = [];
        state.selectedVisits = [];
        state.vehicleOverrides = {};
        state.visitOverrides = {};
        state.settings = defaultSettings();
        state.jobId = null;
        state.solving = false;
        state.vehicleRoutePlan = null;
        state.solutionBaseline = null;
        state.currentSnapshot = null;
        state.savedKey = null;
        state.progress = null;
        state.viewDay = null;
    },
};

function errorMessage(error) {
    if (error && error.response && typeof error.response.data === "string" && error.response.data.trim()) {
        return error.response.data.trim();
    }
    return "The route planner could not be reached";
}

function stopPolling() {
    clearTimeout(pollTimer);
    pollTimer = null;
}

const actions = {
    async startSolve({state, getters, commit, dispatch}, prepared) {
        if (state.solving) return;
        const issues = getters.getProblemIssues;
        if (issues.length > 0) {
            commit("showSnackBar", {color: "error", message: issues[0]}, {root: true});
            return;
        }

        stopPolling();
        commit("setVehicleRoutePlan", null);
        commit("setJobId", null);
        commit("setSolving", true);
        commit("setSolutionBaseline", JSON.stringify(getters.getProblem));
        try {
            const response = await Repository.post(`${CVRP_ROUTING_API}/route-plans`, prepared || getters.getProblem);
            commit("setJobId", response.data);
            dispatch("pollSolve");
        } catch (error) {
            commit("setSolving", false);
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
        }
    },

    // Fetches the best solution so far and keeps doing so until the solver stops.
    async pollSolve({state, commit, dispatch}) {
        stopPolling();
        const jobId = state.jobId;
        if (jobId == null) return;
        try {
            const response = await Repository.get(`${CVRP_ROUTING_API}/route-plans/${jobId}`);
            // a newer solve or a reset replaced this job while the request ran
            if (state.jobId !== jobId) return;
            commit("setVehicleRoutePlan", response.data);
            if (response.data.solver_status === "NOT_SOLVING") {
                commit("setSolving", false);
                return;
            }
            pollTimer = setTimeout(() => dispatch("pollSolve"), POLL_MS);
        } catch (error) {
            if (state.jobId !== jobId) return;
            commit("setSolving", false);
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
        }
    },

    async stopSolve({state, commit}) {
        stopPolling();
        if (state.jobId == null) {
            commit("setSolving", false);
            return;
        }
        try {
            const response = await Repository.delete(`${CVRP_ROUTING_API}/route-plans/${state.jobId}`);
            commit("setVehicleRoutePlan", response.data);
        } catch (error) {
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
        } finally {
            commit("setSolving", false);
        }
    },

    // Drops the solution so the plan can be edited again; the selection stays.
    // A published plan is left as saved: the edits become a new, unsaved plan.
    clearSolution({state, getters, commit}) {
        stopPolling();
        commit("setJobId", null);
        commit("setSolving", false);
        commit("setVehicleRoutePlan", null);
        commit("setSolutionBaseline", null);
        if (getters.isSnapshotLocked) {
            const name = state.currentSnapshot.name;
            commit("setCurrentSnapshot", {snapshot: null, savedKey: null});
            commit("showSnackBar", {
                color: "primary",
                message: `"${name}" is published and stays as it was; save your changes as a new plan`,
            }, {root: true});
        }
    },

    // ----------------------------- saved plans ----------------------------- //

    async loadSavedPlans({commit}, filters) {
        try {
            const response = await Repository.get(`${CVRP_ROUTING_API}/plans`, {params: filters || {}});
            commit("setSavedPlans", response.data);
        } catch (error) {
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
        }
    },

    // Saves the open plan, or a new one when asNew or nothing is open. Resolves
    // to true when saved.
    async saveSnapshot({state, getters, commit, dispatch}, {name, status, notes, asNew, recurrenceDays}) {
        const current = asNew ? null : state.currentSnapshot;
        const repeats = (recurrenceDays || []).length > 0;
        const body = {
            id: current ? current.id : null,
            version: current ? current.version : 0,
            name: name,
            status: status || "draft",
            notes: notes || "",
            is_template: repeats,
            recurrence_days: repeats ? recurrenceDays : [],
            plan_date: state.settings.planDate,
            job_id: state.jobId,
            selection: getters.getSelection,
            problem: getters.getProblem,
            solution: state.vehicleRoutePlan,
        };
        try {
            const response = await Repository.post(`${CVRP_ROUTING_API}/plans`, body);
            commit("setCurrentSnapshot", {snapshot: response.data, savedKey: getters.getSaveKey});
            commit("showSnackBar", {color: "success", message: `Saved "${response.data.name}"`}, {root: true});
            dispatch("loadSavedPlans");
            if (repeats) dispatch("loadTemplates");
            return true;
        } catch (error) {
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
            return false;
        }
    },

    // Loads a saved plan into the planner exactly as it was saved: selection,
    // edits, settings and the solver's answer.
    async openSnapshot({state, getters, commit}, id) {
        if (state.solving) return false;
        try {
            const response = await Repository.get(`${CVRP_ROUTING_API}/plans/${id}`);
            const snapshot = response.data;
            stopPolling();
            commit("setSolving", false);
            commit("setJobId", null);
            commit("setProgress", null);
            commit("restoreSelection", snapshot.selection || {});
            commit("setVehicleRoutePlan", snapshot.solution);
            commit("setSolutionBaseline", snapshot.solution ? JSON.stringify(getters.getProblem) : null);

            const summary = Object.assign({}, snapshot);
            delete summary.selection;
            delete summary.problem;
            delete summary.solution;
            commit("setCurrentSnapshot", {snapshot: summary, savedKey: getters.getSaveKey});
            return true;
        } catch (error) {
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
            return false;
        }
    },

    async setSnapshotStatus({state, getters, commit, dispatch}, status) {
        const current = state.currentSnapshot;
        if (!current) return;
        try {
            const response = await Repository.put(`${CVRP_ROUTING_API}/plans/${current.id}/status`, {status: status, version: current.version});
            commit("setCurrentSnapshot", {snapshot: response.data, savedKey: getters.getSaveKey});
            dispatch("loadSavedPlans");
        } catch (error) {
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
        }
    },

    async duplicateSnapshot({commit, dispatch}, id) {
        try {
            const response = await Repository.post(`${CVRP_ROUTING_API}/plans/${id}/duplicate`);
            dispatch("loadSavedPlans");
            return response.data;
        } catch (error) {
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
            return null;
        }
    },

    async deleteSnapshot({state, commit, dispatch}, id) {
        try {
            await Repository.delete(`${CVRP_ROUTING_API}/plans/${id}`);
            if (state.currentSnapshot && state.currentSnapshot.id === id) {
                commit("setCurrentSnapshot", {snapshot: null, savedKey: null});
            }
            dispatch("loadSavedPlans");
        } catch (error) {
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
        }
    },

    // ---------------------------- templates ---------------------------- //

    async loadTemplates({commit}) {
        try {
            const response = await Repository.get(`${CVRP_ROUTING_API}/plans`, {params: {kind: "template"}});
            commit("setSavedTemplates", response.data);
        } catch (error) {
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
        }
    },

    // Makes a repeating plan's drafts for the coming days; the server solves
    // them in the background and they appear in the plans list.
    async generateDrafts({commit, dispatch}, {id, from, to}) {
        try {
            const response = await Repository.post(`${CVRP_ROUTING_API}/plans/${id}/generate`, {from, to});
            const count = response.data.length;
            commit("showSnackBar", {
                color: "success",
                message: count ? `Made ${count} draft${count > 1 ? "s" : ""}; they are being solved now` : "Those days already have drafts",
            }, {root: true});
            dispatch("loadSavedPlans");
        } catch (error) {
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
        }
    },

    // ------------------------ insert a stop ------------------------ //

    // The solved plan with one more stop, unassigned, for the solver to place.
    // Route geometry is left out: the solver rebuilds it and it is the bulk.
    planWithStop({state, getters}, placeId) {
        const place = getters.getPlaceMap.get(placeId);
        if (!state.vehicleRoutePlan || !place) return null;
        const plan = JSON.parse(JSON.stringify(state.vehicleRoutePlan));
        plan.vehicles.forEach(vehicle => { delete vehicle.geometry_list; });
        if (!plan.visits.some(visit => visit.id === placeId)) {
            plan.visits.push(getters.toVisit(place));
        }
        return plan;
    },

    // Where the stop fits best: up to five {vehicleId, index, scoreDiff}.
    async recommendStop({commit, dispatch}, placeId) {
        const solution = await dispatch("planWithStop", placeId);
        if (!solution) return [];
        try {
            const response = await Repository.post(`${CVRP_ROUTING_API}/route-plans/recommendation`, {solution, visitId: placeId});
            return response.data;
        } catch (error) {
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
            return [];
        }
    },

    // Inserts the stop where chosen and keeps the result as the solution; the
    // place joins the selection so the plan and its answer still match.
    async applyStop({state, getters, commit, dispatch}, {placeId, vehicleId, index}) {
        const solution = await dispatch("planWithStop", placeId);
        if (!solution) return false;
        try {
            const response = await Repository.post(`${CVRP_ROUTING_API}/route-plans/recommendation/apply`,
                {solution, visitId: placeId, vehicleId, index});
            if (!state.selectedVisits.includes(placeId)) {
                commit("setSelectedVisits", [...state.selectedVisits, placeId]);
            }
            commit("setVehicleRoutePlan", response.data);
            commit("setSolutionBaseline", JSON.stringify(getters.getProblem));
            return true;
        } catch (error) {
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
            return false;
        }
    },

    // ------------------------- progress ------------------------- //

    async loadProgress({state, commit}) {
        const snapshot = state.currentSnapshot;
        if (!snapshot) return;
        try {
            const response = await Repository.get(`${CVRP_ROUTING_API}/plans/${snapshot.id}/progress`);
            // another plan was opened while this loaded
            if (state.currentSnapshot && state.currentSnapshot.id === snapshot.id) {
                commit("setProgress", response.data);
            }
        } catch (error) {
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
        }
    },

    // Re-solves the rest of the day: each vehicle keeps the stops it has
    // already made (pinned, in order) and the solver re-plans what is left.
    async replanRemaining({state, getters, commit, dispatch}) {
        const progress = state.progress;
        const solution = state.vehicleRoutePlan;
        if (!progress || !solution) return;
        const donePrefix = new Map(progress.vehicles.map(vehicle => [vehicle.vehicle_id, vehicle.done_prefix]));
        const routes = new Map(solution.vehicles.map(vehicle => [vehicle.id, vehicle.visits || []]));
        const problem = JSON.parse(JSON.stringify(getters.getProblem));
        const planned = new Set(problem.visits.map(visit => visit.id));
        problem.vehicles.forEach(vehicle => {
            const route = (routes.get(vehicle.id) || []).filter(id => planned.has(id));
            vehicle.visits = route;
            vehicle.pinned_visit_count = Math.min(donePrefix.get(vehicle.id) || 0, route.length);
        });
        const pinned = problem.vehicles.reduce((sum, vehicle) => sum + vehicle.pinned_visit_count, 0);
        commit("showSnackBar", {color: "primary", message: `Re-planning with ${pinned} completed stop${pinned === 1 ? "" : "s"} kept in place`}, {root: true});
        await dispatch("startSolve", problem);
    },

    // -------------------------- sheets -------------------------- //

    async downloadSheets({state, commit}, vehicleId) {
        const snapshot = state.currentSnapshot;
        if (!snapshot) return;
        try {
            const response = await Repository.get(`${CVRP_ROUTING_API}/plans/${snapshot.id}/sheets`,
                {params: vehicleId ? {vehicle: vehicleId} : {}, responseType: "blob"});
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `Route sheets - ${snapshot.name}.pdf`;
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (error) {
            // a blob error body has to be read as text
            let message = "Could not make the route sheets";
            if (error && error.response && error.response.data && error.response.data.text) {
                message = (await error.response.data.text()).trim() || message;
            }
            commit("showSnackBar", {color: "error", message: message}, {root: true});
        }
    },

    // ------------------------- defaults ------------------------- //

    // Writes planner edits onto the vehicle and place records so the next
    // plan starts from them. {vehicles: {id: details}, places: {id: details}}
    async saveDefaults({commit, dispatch}, payload) {
        try {
            const response = await Repository.post(`${CVRP_ROUTING_API}/defaults`, payload);
            await Promise.all([
                Object.keys(payload.vehicles || {}).length ? dispatch("vehicles/devices/loadAllDevices", null, {root: true}) : null,
                Object.keys(payload.places || {}).length ? dispatch("place/loadAllPlaces", null, {root: true}) : null,
            ]);
            commit("showSnackBar", {color: "success", message: `Saved as defaults on ${response.data.updated} record${response.data.updated === 1 ? "" : "s"}`}, {root: true});
            return true;
        } catch (error) {
            commit("showSnackBar", {color: "error", message: errorMessage(error)}, {root: true});
            return false;
        }
    },

    // Starts an empty plan: every positioned vehicle, no places, default settings.
    newPlan({state, getters, commit}) {
        if (state.solving) return;
        stopPolling();
        commit("reset");
        commit("setSelectedVehicles", getters.getPlannableDevices.map(device => device.vehicle_id));
    },

    leavePlanner() {
        stopPolling();
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
